// Уведомления человека: привязка Telegram (п.11) и настройки каналов предупреждений
// о сроках (T-046) — состояние и управление.
//
// Личная настройка, а не настройка точки: уведомления приходят человеку в его чат
// и на его телефоны. Поэтому эндпоинты без параметров — как `/tenants/me/`, — и спросить
// о чужих настройках через них нельзя по построению.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { httpClient, HttpError, isUndelivered } from '@/shared/api';
import { t } from '@/shared/i18n';
import { rememberLegacyPushServer } from '@/shared/push';

import type { AlertSettings, AlertSettingsPatch } from './alertSettingsTypes';

/** Чат, привязанный к учётке (T-058): у общей учётки точки их несколько. */
export type TelegramChat = {
  readonly id: string;
  /** Имя из профиля Telegram — есть всегда, в отличие от @username. */
  readonly telegram_name: string;
  readonly telegram_username: string;
  /** Предупреждения заранее в этот чат; пороги — общие для учётки. */
  readonly alerts: boolean;
  /** Утренняя сводка в этот чат — только у руководства (`digest_available`). */
  readonly digest: boolean;
  readonly created_at: string;
};

export type TelegramConnection = {
  readonly connected: boolean;
  readonly telegram_username: string;
  /**
   * Чаты учётки (T-058). Нет поля — сервер старше: у него один чат, и окно работает
   * по-старому (`connected` и «Отключить» всё сразу).
   */
  readonly chats?: readonly TelegramChat[];
  /** Учётке уходит утренняя сводка — показывать ли галочку «Сводка» у чатов. */
  readonly digest_available?: boolean;
  /** Привязались ли по ссылке из `?link=` — только когда о ней спросили. */
  readonly link_used?: boolean;
  /** Бот настроен на этой установке. Иначе подключать нечего — кнопку не показываем. */
  readonly available: boolean;
  /**
   * Предупреждения заранее о сроках (решение владельца 2026-09-26) — только по желанию.
   * Необязательное: сервер старше этой версии поля не отдаёт. Читается только
   * в прежнем режиме — новый берёт выбор из alert-settings.
   */
  readonly expiry_alerts?: boolean;
};

const KEY = ['telegram-connection'] as const;
const ALERT_SETTINGS = '/tenants/me/alert-settings/';

/** Ключ в кэше; токен сессии в него не попадает — кэш переживает смену сессии в памяти. */
export const alertSettingsKey = ['notifications', 'alert-settings'] as const;

/**
 * Состояние привязки. `waiting` — ссылка выдана, человек привязывает Telegram на
 * телефоне: окно раз в 3 с спрашивает, не привязался ли он. Иначе на общем планшете
 * кухни окно так и показывало бы QR-код после того, как всё уже подключено.
 *
 * `linkId` — номер выданной ссылки (T-058): чаты у учётки уже есть, и «подключено»
 * значит «привязались ИМЕННО по этой ссылке» (`link_used`), а не `connected`.
 */
export function useTelegramConnection(
  options: {
    readonly waiting?: boolean;
    readonly enabled?: boolean;
    readonly linkId?: string | null;
  } = {},
) {
  const linkId = options.linkId ?? null;
  return useQuery({
    queryKey: linkId ? [...KEY, linkId] : KEY,
    queryFn: () =>
      httpClient.get<TelegramConnection>(
        linkId
          ? `/tenants/me/telegram/?link=${encodeURIComponent(linkId)}`
          : '/tenants/me/telegram/',
      ),
    // Опрос — пока ссылка ждёт: привязались по ней (у прежнего сервера — просто
    // «подключено») — дальше спрашивать незачем.
    refetchInterval: (query) => (options.waiting && !boundByLink(query.state.data) ? 3000 : false),
    enabled: options.enabled ?? true,
    // Смена ссылки меняет ключ: список чатов остаётся на экране, пока не придёт ответ.
    // Без `link_used` прежней ссылки — иначе новая выглядела бы уже использованной.
    placeholderData: (previous) => (previous ? { ...previous, link_used: undefined } : undefined),
  });
}

function boundByLink(data: TelegramConnection | undefined): boolean {
  if (data === undefined) return false;
  return data.chats === undefined ? data.connected : data.link_used === true;
}

/** Выданная ссылка привязки. `link_id` нет у сервера старше T-058. */
export type TelegramLink = { readonly link: string; readonly link_id?: string };

/**
 * Выпустить ссылку привязки.
 *
 * Мутация, а не запрос: окно, открытое «посмотреть», не должно плодить ссылки. Прежний
 * сервер к тому же гасил предыдущую ссылку при каждом выпуске.
 */
export function useIssueTelegramLink() {
  return useMutation({
    mutationFn: () => httpClient.post<TelegramLink>('/tenants/me/telegram/'),
  });
}

/** Перечитать чаты и настройки каналов: «включено» у Telegram выводится из чатов. */
function refreshTelegram(client: ReturnType<typeof useQueryClient>): Promise<unknown> {
  return Promise.all([
    client.invalidateQueries({ queryKey: KEY }),
    client.invalidateQueries({ queryKey: alertSettingsKey }),
  ]);
}

export type TelegramChatPatch = { readonly alerts?: boolean; readonly digest?: boolean };

/** Галочки одного чата (T-058). Снятую чат узнает сообщением от бота. */
export function useUpdateTelegramChat() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { readonly id: string; readonly patch: TelegramChatPatch }) =>
      httpClient.patch<TelegramChat>(`/tenants/me/telegram/chats/${id}/`, patch),
    onSettled: () => refreshTelegram(client),
  });
}

/** Отключить один чат (T-058). Бот сообщит ему «вас отключили». */
export function useDisconnectTelegramChat() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => httpClient.delete<void>(`/tenants/me/telegram/chats/${id}/`),
    onSettled: () => refreshTelegram(client),
  });
}

/** Прежний режим: включить или выключить предупреждения заранее о сроках. */
export function useSetTelegramAlerts() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      httpClient.patch<TelegramConnection>('/tenants/me/telegram/', { expiry_alerts: enabled }),
    onSuccess: (data) => client.setQueryData(KEY, data),
  });
}

export function useDisconnectTelegram() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => httpClient.delete<void>('/tenants/me/telegram/'),
    // Отключение выключает и канал (T-046): галочка и сетка Telegram — из alert-settings.
    onSuccess: () => refreshTelegram(client),
  });
}

// --- Настройки каналов (T-046) --------------------------------------------------------

/**
 * Сервер старше этой версии: alert-settings ему неизвестны (404).
 *
 * Бывает при нарушенном порядке выката (сервер → веб-бандл). Окно тогда работает
 * по-старому — через `/me/telegram/` и прежние эндпоинты push, — а не показывает ошибку.
 */
export type LegacyAlertSettings = { readonly legacy: true };

export type AlertSettingsState = AlertSettings | LegacyAlertSettings;

export function isLegacy(state: AlertSettingsState): state is LegacyAlertSettings {
  return 'legacy' in state;
}

/**
 * Ответ сервера — действительно настройки каналов.
 *
 * Проверка на границе, а не по всему окну: ответ читают полоса навигации и хук входа,
 * и чужой ответ с кодом 200 (прокси, портал Wi-Fi, сервер не той версии) ронял бы
 * весь верх экрана на `undefined.available`. Непохожий ответ — отказ загрузки.
 */
function asAlertSettings(value: unknown): AlertSettings {
  if (value !== null && typeof value === 'object') {
    const { available_tiers: tiers, telegram, push } = value as Record<string, unknown>;
    if (Array.isArray(tiers) && isChannel(telegram) && isChannel(push)) {
      return value as AlertSettings;
    }
  }
  throw new Error('alert-settings: ответ сервера не похож на настройки уведомлений');
}

function isChannel(value: unknown): boolean {
  return (
    value !== null &&
    typeof value === 'object' &&
    Array.isArray((value as { tiers?: unknown }).tiers)
  );
}

async function fetchAlertSettings(): Promise<AlertSettingsState> {
  try {
    const settings = asAlertSettings(await httpClient.get<unknown>(ALERT_SETTINGS));
    rememberLegacyPushServer(false);
    return settings;
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) {
      // Выходу нужно знать то же самое: прежнему серверу телефон при выходе снимают сами.
      rememberLegacyPushServer(true);
      return { legacy: true };
    }
    throw error;
  }
}

/** Как часто переспрашивать настройки, пока запрос до сервера не доходит. */
const UNDELIVERED_REFETCH_MS = 30_000;

/**
 * Выбор и пороги каналов человека. Не персистится на диск (`queryPersist`): настройки
 * меняются только онлайн, а без сети окно показывает последние известные из памяти.
 *
 * Без повторов транспорта: три попытки с паузами держали бы окно без сети в «Загрузка…»
 * вместо подписи «Нет сети». Недошедший запрос переспрашивается раз в 30 с — у ЛЮБОГО
 * наблюдателя, а не только у открытого окна: пункт меню, не получивший ответа при
 * запуске без сети, иначе не появлялся бы до перезапуска, а push не подключался бы
 * (`navigator.onLine` на кухонном WiFi врёт, и события `online` может не быть).
 */
export function useAlertSettings(
  options: {
    readonly enabled?: boolean;
    /**
     * Спросить сервер при монтировании, даже если ответ свежий: окно показывает выбор,
     * который могли сменить с другого телефона минуту назад (B-039, сценарий 13).
     */
    readonly fresh?: boolean;
  } = {},
) {
  return useQuery({
    queryKey: alertSettingsKey,
    queryFn: fetchAlertSettings,
    enabled: options.enabled ?? true,
    refetchOnMount: options.fresh ? 'always' : true,
    retry: false,
    refetchInterval: (query) => (isUndelivered(query.state.error) ? UNDELIVERED_REFETCH_MS : false),
  });
}

export interface UpdateAlertSettingsOptions {
  /**
   * Сервер подтвердил изменение. Вызывается и после закрытия окна — в отличие от
   * колбэков `mutate`, которые TanStack Query у размонтированного окна не зовёт.
   */
  readonly onSaved?: (saved: AlertSettings, patch: AlertSettingsPatch) => void;
}

/**
 * Сохранить изменение ОДНОГО канала. Ответ сервера — всё состояние — кладётся в кэш.
 *
 * Оптимистичного обновления нет: галочка встаёт, когда сервер подтвердил. Второго
 * сохранения во время первого тоже нет — пока идёт запрос, окно держит все галочки
 * выключенными (`ChannelSections`).
 */
export function useUpdateAlertSettings(options: UpdateAlertSettingsOptions = {}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (patch: AlertSettingsPatch) =>
      asAlertSettings(await httpClient.patch<unknown>(ALERT_SETTINGS, patch)),
    // Перезапрос, начатый до сохранения, ответил бы прежним состоянием ПОСЛЕ ответа
    // PATCH — и галочка откатывалась бы на глазах (ревью B-039). Отменённый перезапрос
    // в кэш не пишет.
    onMutate: () => client.cancelQueries({ queryKey: alertSettingsKey }),
    onSuccess: (data, patch) => {
      options.onSaved?.(data, patch);
      client.setQueryData<AlertSettingsState>(alertSettingsKey, data);
    },
    // Отказ — повод переспросить сервер: 409 (Telegram отключили с другого устройства,
    // с сервера убрали ключ Firebase) меняет то, что окно вправе показывать, а недошедший
    // запрос мог и сохраниться. Без `await`: мутация до конца перезапроса оставалась бы
    // «идёт», и окно держало бы все галочки выключенными, пока не ответит сеть.
    onError: (error) => {
      void client.invalidateQueries({ queryKey: alertSettingsKey });
      if (error instanceof HttpError && error.status === 409) {
        void client.invalidateQueries({ queryKey: KEY });
      }
    },
  });
}

/** Канал, изменение которого было в запросе, — чтобы показать отказ рядом с ним. */
export function channelOf(patch: AlertSettingsPatch | undefined): 'telegram' | 'push' | null {
  if (patch?.telegram) return 'telegram';
  if (patch?.push) return 'push';
  return null;
}

/**
 * Текст отказа сохранения.
 *
 * Сервер объясняет сам: 400 (`code: invalid`) — сообщением по полю в
 * `details.<канал>.tiers`, 409 и 503 — в `message`. По коду строки не ветвимся:
 * текст отказа — забота сервера, и новый отказ не должен требовать нового бандла.
 * Нет ответа сервера (сеть) — общий «не сохранилось, проверьте связь».
 */
export function saveErrorText(error: unknown, channel: 'telegram' | 'push' | null): string {
  if (error instanceof HttpError) {
    const envelope = (error.payload as { error?: { message?: unknown; details?: unknown } })?.error;
    const details = envelope?.details as Record<string, unknown> | undefined;
    const text =
      (channel !== null ? firstText(details?.[channel]) : null) ??
      firstText(details) ??
      (typeof envelope?.message === 'string' && envelope.message ? envelope.message : null);
    if (text) return text;
  }
  return t('alerts.saveFailed');
}

/** Первая строка в `details` DRF: `["текст"]`, `{tiers: ["текст"]}` и так далее. */
function firstText(value: unknown): string | null {
  if (typeof value === 'string') return value || null;
  if (value === null || typeof value !== 'object') return null;
  for (const item of Array.isArray(value) ? value : Object.values(value)) {
    const text = firstText(item);
    if (text) return text;
  }
  return null;
}
